import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Bell, X, ArrowRight, Globe2, Sparkles, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "../components/Logo";
import InboxBell from "../components/InboxBell";
import OnboardingChecklist from "../components/OnboardingChecklist";
import UserAvatar from "../components/UserAvatar";
import StatCounter from "../components/StatCounter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Copy dictionary (EN / FR) ----
const COPY = {
  en: {
    welcome: "The Archive",
    headline: "Knowledge, preserved.",
    // Subheadline is composed inline so the counts can animate — see JSX below.
    sub_prefix: "Discover & search",
    sub_between: "scientific books and papers —",
    sub_pages_suffix: "pages of knowledge.",
    stat_documents: "documents",
    stat_pages: "pages indexed",
    stat_languages: "languages",
    stat_countries: "countries reached",
    search_placeholder: "Search authors, titles, or DOIs…",
    cta_search: "Start Searching",
    cta_browse: "Browse the Library",
    nav_login: "Login",
    nav_register: "Register",
    nav_dashboard: "Dashboard",
    nav_logout: "Logout",
    read_more: "Read more",
    book_label_papers: "Papers",
    book_label_books: "Books",
    book_label_network: "Citations",
    researcher_kicker: "AI Researcher",
    researcher_title: "Turn a research question into a cited draft.",
    researcher_desc:
      "Describe your topic and the AI Researcher writes a structured literature draft grounded exclusively on documents from The Archive — every claim cites a real, verifiable source.",
    researcher_b1: "Grounded on the library — citations like [S1] link to real documents",
    researcher_b2: "AI revisions: \u201cexpand the methods section\u201d, \u201ctighten the abstract\u201d",
    researcher_b3: "Export as an academic PDF or an editable Word file",
    researcher_b4: "Share a read-only link with your supervisor or colleagues",
    researcher_cta: "Try it — first draft is free",
    researcher_note: "Included with every subscription plan.",
    oa_kicker: "New in the library",
    cat_kicker: "Browse by category",
    cat_title: "Every shelf at a glance",
    cat_viewall: "View all categories",
    cat_docs: "documents",
    cat_doc: "document",
    oa_title: "Fresh open-access arrivals",
    oa_desc: "Peer-reviewed open-access works recently added by our curators — free to explore alongside the core collection.",
    oa_badge: "Open access",
    oa_full_text: "Full text",
    footer_about: "About",
    footer_terms: "Terms",
    footer_contact: "Contact",
    footer_rights: "© 2026 The Archive. A scholarly digital library.",
  },
  fr: {
    welcome: "Les Archives",
    headline: "Le savoir, préservé.",
    sub_prefix: "Découvrez et recherchez",
    sub_between: "livres et articles scientifiques —",
    sub_pages_suffix: "pages de savoir.",
    stat_documents: "documents",
    stat_pages: "pages indexées",
    stat_languages: "langues",
    stat_countries: "pays touchés",
    search_placeholder: "Rechercher auteurs, titres ou DOI…",
    cta_search: "Commencer la recherche",
    cta_browse: "Parcourir la bibliothèque",
    nav_login: "Connexion",
    nav_register: "S'inscrire",
    nav_dashboard: "Tableau de bord",
    nav_logout: "Déconnexion",
    read_more: "Lire la suite",
    book_label_papers: "Articles",
    book_label_books: "Livres",
    book_label_network: "Citations",
    researcher_kicker: "Chercheur IA",
    researcher_title: "Transformez une question de recherche en brouillon cité.",
    researcher_desc:
      "Décrivez votre sujet et le Chercheur IA rédige un brouillon structuré fondé exclusivement sur les documents des Archives — chaque affirmation cite une source réelle et vérifiable.",
    researcher_b1: "Ancré dans la bibliothèque — les citations [S1] renvoient à de vrais documents",
    researcher_b2: "Révisions IA : « développez la méthodologie », « condensez le résumé »",
    researcher_b3: "Export en PDF académique ou en fichier Word modifiable",
    researcher_b4: "Partagez un lien en lecture seule avec votre directeur ou vos collègues",
    researcher_cta: "Essayez — le premier brouillon est gratuit",
    researcher_note: "Inclus dans tous les abonnements.",
    oa_kicker: "Nouveautés",
    cat_kicker: "Parcourir par catégorie",
    cat_title: "Toutes les étagères en un coup d'œil",
    cat_viewall: "Voir toutes les catégories",
    cat_docs: "documents",
    cat_doc: "document",
    oa_title: "Nouvelles arrivées en libre accès",
    oa_desc: "Travaux en libre accès récemment ajoutés par nos curateurs — à explorer librement aux côtés de la collection principale.",
    oa_badge: "Libre accès",
    oa_full_text: "Texte intégral",
    footer_about: "À propos",
    footer_terms: "Conditions",
    footer_contact: "Contact",
    footer_rights: "© 2026 Les Archives. Une bibliothèque numérique savante.",
  },
};

const SERIF = { fontFamily: "'Cormorant Garamond', serif" };

// ============================================================================
// Main HomePage
// ============================================================================
const HomePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(
    () => localStorage.getItem("archive_lang") || "en"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [announcement, setAnnouncement] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [totalItems, setTotalItems] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [totalCountries, setTotalCountries] = useState(null);
  const [oaItems, setOaItems] = useState([]);
  const [homeCategories, setHomeCategories] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then((r) => setHomeCategories((r.data?.categories || []).slice(0, 6)))
      .catch(() => {});
  }, []);
  const t = COPY[lang];

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/announcements/latest`)
      .then((r) => {
        if (!cancelled && r.data) setAnnouncement(r.data);
      })
      .catch(() => {});
    axios
      .get(`${API}/public/stats`)
      .then((r) => {
        if (cancelled) return;
        if (typeof r.data?.total_items === "number") setTotalItems(r.data.total_items);
        if (typeof r.data?.total_pages === "number") setTotalPages(r.data.total_pages);
        if (typeof r.data?.total_countries === "number") setTotalCountries(r.data.total_countries);
      })
      .catch(() => {});
    axios
      .get(`${API}/open-access/recent`)
      .then((r) => {
        if (!cancelled) setOaItems(r.data?.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const numberLocale = lang === "fr" ? "fr-FR" : "en-US";

  const changeLang = (next) => {
    setLang(next);
    localStorage.setItem("archive_lang", next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: "#F9F6F0",
        color: "#0F172A",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
      data-testid="home-welcome-page"
    >
      {/* ---- Announcement banner ---- */}
      {announcement && showBanner && (
        <div
          className="py-2 px-4 text-white"
          style={{ background: "#0F172A" }}
          data-testid="announcement-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Bell className="w-4 h-4 flex-shrink-0 opacity-80" />
              <span className="text-sm font-medium truncate">
                {announcement.subject}
              </span>
              <button
                onClick={() => navigate("/announcements")}
                className="text-xs underline hover:no-underline flex-shrink-0 opacity-90 hover:opacity-100"
              >
                {t.read_more}
              </button>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="p-1 hover:bg-white/10 rounded"
              aria-label="Close announcement"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ---- Sticky nav ---- */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{
          backgroundColor: "rgba(249, 246, 240, 0.85)",
          borderColor: "rgba(15, 23, 42, 0.10)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
            data-testid="home-nav-logo"
          >
            <Logo size={36} />
            <span
              className="text-2xl font-medium tracking-tight hidden sm:block"
              style={SERIF}
            >
              {t.welcome}
            </span>
          </button>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Language toggle */}
            <div
              className="flex items-center text-xs font-semibold tracking-[0.18em] uppercase"
              data-testid="home-language-toggle"
            >
              <button
                onClick={() => changeLang("en")}
                data-testid="home-language-toggle-en"
                className={`px-2.5 py-1 transition-colors ${
                  lang === "en"
                    ? "text-[#0F172A]"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                EN
              </button>
              <span className="text-slate-300">/</span>
              <button
                onClick={() => changeLang("fr")}
                data-testid="home-language-toggle-fr"
                className={`px-2.5 py-1 transition-colors ${
                  lang === "fr"
                    ? "text-[#0F172A]"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                FR
              </button>
            </div>

            <span className="h-6 w-px bg-slate-300/70 mx-1 hidden md:block" />

            {user ? (
              <>
                <InboxBell />
                <button
                  onClick={() => navigate("/profile")}
                  title="My Profile"
                  data-testid="home-nav-avatar"
                  className="rounded-full ring-2 ring-transparent hover:ring-orange-300 transition-shadow"
                >
                  <UserAvatar user={user} size={32} />
                </button>
                <Button
                  onClick={() =>
                    navigate(user.role === "admin" ? "/admin" : "/profile")
                  }
                  data-testid="home-nav-dashboard"
                  className="rounded-full text-white hover:opacity-90"
                  style={{ backgroundColor: "#9A3412" }}
                  size="sm"
                >
                  {t.nav_dashboard}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button
                  onClick={handleLogout}
                  data-testid="home-nav-logout"
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-slate-900 hidden md:inline-flex"
                >
                  {t.nav_logout}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/login")}
                  variant="ghost"
                  size="sm"
                  data-testid="home-nav-login"
                  className="text-slate-700 hover:text-slate-900"
                >
                  {t.nav_login}
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  data-testid="home-nav-register"
                  size="sm"
                  className="rounded-full text-white hover:opacity-90"
                  style={{ backgroundColor: "#9A3412" }}
                >
                  {t.nav_register}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <main className="flex-1">
        <section
          className="relative overflow-hidden"
          data-testid="home-hero-section"
        >
          {/* Full-width background photo */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/library-hero.jpg')" }}
            aria-hidden="true"
          />
          {/* Parchment-tinted gradient overlay for legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(105deg, rgba(249,246,240,0.96) 0%, rgba(249,246,240,0.88) 38%, rgba(249,246,240,0.55) 65%, rgba(249,246,240,0.20) 100%)",
            }}
            aria-hidden="true"
          />

          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 lg:py-32">
            <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left: copy + search */}
              <div className="col-span-12 lg:col-span-7">
                <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#9A3412] mb-6">
                  {t.welcome} &nbsp;·&nbsp; EST. 2024
                </p>
                <h1
                  className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] mb-8"
                  style={SERIF}
                  data-testid="home-headline"
                >
                  {t.headline}
                </h1>
                <p
                  className="text-lg md:text-xl text-slate-700 max-w-xl mb-12 leading-relaxed"
                  data-testid="home-subheadline"
                >
                  {t.sub_prefix}{" "}
                  <StatCounter
                    value={totalItems}
                    locale={numberLocale}
                    roundAt={1000}
                    className="font-semibold text-slate-900"
                    placeholder={lang === "fr" ? "des milliers de" : "thousands of"}
                    testId="home-stat-items"
                  />{" "}
                  {t.sub_between}{" "}
                  <StatCounter
                    value={totalPages}
                    locale={numberLocale}
                    roundAt={10000}
                    className="font-semibold text-slate-900"
                    placeholder={lang === "fr" ? "des millions de" : "millions of"}
                    testId="home-stat-pages"
                  />{" "}
                  {t.sub_pages_suffix}
                </p>

                {/* Architectural search bar */}
                <form
                  onSubmit={handleSearch}
                  className="max-w-2xl"
                  data-testid="home-search-form"
                >
                  <div
                    className="flex items-center gap-4 py-3 border-b-2 transition-colors focus-within:border-[#9A3412] backdrop-blur-sm"
                    style={{ borderColor: "#0F172A" }}
                  >
                    <Search className="w-6 h-6 text-slate-500 shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t.search_placeholder}
                      data-testid="home-search-input"
                      className="flex-1 bg-transparent outline-none text-xl md:text-2xl placeholder:text-slate-400 py-2"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    />
                    <button
                      type="submit"
                      data-testid="home-search-submit"
                      className="hidden sm:inline-flex items-center justify-center w-11 h-11 rounded-full text-white hover:opacity-90 transition-opacity shrink-0"
                      style={{ backgroundColor: "#9A3412" }}
                      aria-label={t.cta_search}
                    >
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </form>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-4 mt-10">
                  <Button
                    onClick={handleSearch}
                    data-testid="home-cta-start-searching"
                    className="rounded-full text-white hover:opacity-90 px-7 py-6 text-base font-medium tracking-wide"
                    style={{ backgroundColor: "#9A3412" }}
                  >
                    {t.cta_search}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate("/search")}
                    data-testid="home-cta-browse"
                    variant="ghost"
                    className="rounded-full px-7 py-6 text-base font-medium tracking-wide hover:bg-slate-200/60"
                    style={{ color: "#0F172A" }}
                  >
                    {t.cta_browse}
                  </Button>
                </div>
              </div>

              {/* Right column intentionally empty — illustrated books removed per user request */}
            </div>
          </div>
        </section>

        {/* ---- Onboarding checklist for new users ---- */}
        <OnboardingChecklist user={user} lang={lang} />

        {/* ---- AI Researcher showcase ---- */}
        <section
          className="relative overflow-hidden"
          style={{ backgroundColor: "#0F172A" }}
          data-testid="home-researcher-showcase"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <div className="grid grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Copy */}
              <div className="col-span-12 lg:col-span-6">
                <p className="text-xs font-semibold tracking-[0.3em] uppercase text-orange-400 mb-5 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> {t.researcher_kicker}
                </p>
                <h2
                  className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-[1.08] mb-6"
                  style={SERIF}
                  data-testid="home-researcher-title"
                >
                  {t.researcher_title}
                </h2>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-7 max-w-xl">
                  {t.researcher_desc}
                </p>
                <ul className="space-y-3 mb-9">
                  {[t.researcher_b1, t.researcher_b2, t.researcher_b3, t.researcher_b4].map((line) => (
                    <li key={line} className="flex gap-3 text-sm md:text-base text-slate-200">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                      {line}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    onClick={() => navigate("/research")}
                    data-testid="home-researcher-cta"
                    className="rounded-full text-white hover:opacity-90 px-7 py-6 text-base font-medium tracking-wide"
                    style={{ backgroundColor: "#EA580C" }}
                  >
                    {t.researcher_cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <span className="text-xs text-slate-400">{t.researcher_note}</span>
                </div>
              </div>

              {/* Stylized draft mock */}
              <div className="col-span-12 lg:col-span-6" aria-hidden="true">
                <div className="rounded-xl border border-slate-700 bg-slate-900/80 shadow-2xl p-6 md:p-8 max-w-lg mx-auto">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-orange-400 mb-3">
                    Literature review · APA
                  </p>
                  <p className="text-lg text-white mb-4" style={SERIF}>
                    Cobalt Recovery from Copper–Cobalt Ores: A Review
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="h-2 rounded bg-slate-700 w-full" />
                    <div className="h-2 rounded bg-slate-700 w-11/12" />
                    <div className="flex items-center gap-2">
                      <div className="h-2 rounded bg-slate-700 w-7/12" />
                      <span className="text-[10px] font-semibold text-orange-400 bg-orange-400/10 border border-orange-400/30 rounded px-1.5 py-0.5">[S1]</span>
                      <span className="text-[10px] font-semibold text-orange-400 bg-orange-400/10 border border-orange-400/30 rounded px-1.5 py-0.5">[S3]</span>
                    </div>
                    <div className="h-2 rounded bg-slate-700 w-10/12" />
                    <div className="flex items-center gap-2">
                      <div className="h-2 rounded bg-slate-700 w-5/12" />
                      <span className="text-[10px] font-semibold text-orange-400 bg-orange-400/10 border border-orange-400/30 rounded px-1.5 py-0.5">[S2]</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">References</p>
                    <div className="space-y-1.5">
                      <div className="h-1.5 rounded bg-slate-800 w-10/12" />
                      <div className="h-1.5 rounded bg-slate-800 w-9/12" />
                      <div className="h-1.5 rounded bg-slate-800 w-11/12" />
                    </div>
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">8 library sources · verified citations</span>
                    <div className="flex gap-2">
                      <span className="text-[10px] text-slate-300 border border-slate-600 rounded px-2 py-1">PDF</span>
                      <span className="text-[10px] text-slate-300 border border-slate-600 rounded px-2 py-1">Word</span>
                      <span className="text-[10px] text-slate-300 border border-slate-600 rounded px-2 py-1">Share</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---- New arrivals: open access ---- */}
        {oaItems.length > 0 && (
          <section className="bg-white border-b border-slate-200" data-testid="home-oa-shelf">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-sky-700 mb-3 flex items-center gap-2">
                <Globe2 className="w-4 h-4" /> {t.oa_kicker}
              </p>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2" style={SERIF} data-testid="home-oa-title">
                {t.oa_title}
              </h2>
              <p className="text-sm text-slate-500 mb-8 max-w-xl">{t.oa_desc}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {oaItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(`/items/${item.id}`)}
                    className="text-left rounded-lg border border-slate-200 bg-white p-4 hover:border-sky-300 hover:shadow-sm transition-all group"
                    data-testid={`home-oa-item-${item.id}`}
                  >
                    <span className="inline-block rounded-full bg-sky-50 border border-sky-200 text-sky-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide mb-2">{t.oa_badge}</span>
                    <p className="font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-sky-800 transition-colors" style={SERIF}>{item.title}</p>
                    <p className="text-xs text-slate-500 mt-1.5 line-clamp-1">{(item.authors || []).join(", ") || "—"}</p>
                    <div className="flex items-center gap-2 mt-2.5 text-[11px] text-slate-400">
                      {item.year && <span>{item.year}</span>}
                      {item.has_file && <span className="rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 font-medium">{t.oa_full_text}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---- Browse by category ---- */}
        {homeCategories.length > 0 && (
          <section className="border-b border-slate-200" style={{ background: "#f7f6f2" }} data-testid="home-categories-shelf">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-orange-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" /> {t.cat_kicker}
              </p>
              <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900" style={SERIF}>{t.cat_title}</h2>
                <button
                  type="button"
                  onClick={() => navigate("/categories")}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-orange-700 hover:text-orange-900"
                  data-testid="home-categories-viewall"
                >
                  {t.cat_viewall} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {homeCategories.map((cat) => (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => navigate(`/category/${encodeURIComponent(cat.category)}`)}
                    className="group bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3 text-left hover:border-orange-400 hover:shadow-md transition-all"
                    data-testid={`home-category-${cat.category}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {(cat.thumbnails || []).length > 0 ? (
                        <div className="flex -space-x-4 shrink-0 py-1">
                          {cat.thumbnails.map((tid, i) => (
                            <img
                              key={tid}
                              src={`${API}/items/${tid}/thumbnail`}
                              alt=""
                              loading="lazy"
                              className="w-9 h-12 object-cover rounded border-2 border-white shadow-sm bg-slate-100"
                              style={{ zIndex: 3 - i, transform: `rotate(${(i - 1) * 5}deg)` }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="w-9 h-9 shrink-0 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                          <Tag className="w-4 h-4" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate" style={SERIF}>{cat.category}</p>
                        <p className="text-xs text-slate-500">{cat.count} {cat.count === 1 ? t.cat_doc : t.cat_docs}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 shrink-0 text-slate-300 group-hover:text-orange-600 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---- SEO-friendly library description ---- */}
        <section
          className="border-t"
          style={{ borderColor: "rgba(15, 23, 42, 0.10)" }}
          data-testid="home-about-library"
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 text-slate-700">
            <h2
              className="text-2xl md:text-3xl font-semibold mb-6"
              style={SERIF}
            >
              A curated library for engineering &amp; scientific research
            </h2>
            <p className="text-base leading-relaxed mb-4">
              The Archive by <strong>2RA-Edition</strong> is a growing collection of over 8,000 books,
              peer-reviewed articles, theses, and technical reports focused on <strong>extractive metallurgy</strong>,
              <strong> mining engineering</strong>, <strong>hydrometallurgy</strong>,
              <strong> cobalt and copper processing</strong>, <strong>industrial chemistry</strong>,
              and applied scientific research. Every uploaded document is indexed for full-text search, tagged with AI-generated
              metadata, and enriched with abstracts translated into ten languages.
            </p>
            <p className="text-base leading-relaxed mb-4">
              Whether you&apos;re preparing a literature review for a metallurgical thesis, sourcing operating data
              for a hydrometallurgical circuit, benchmarking flotation performance, or building a reading list
              for a graduate course, The Archive brings the technical literature you need into one searchable place.
              Use <a href="/search" className="text-orange-600 hover:underline">Search</a> for keyword lookups,
              open <a href="/smart-search" className="text-orange-600 hover:underline">Smart Search</a> for faceted filtering by year, language, category, and organization,
              or browse curated <a href="/collections" className="text-orange-600 hover:underline">Collections</a> shared by other researchers.
            </p>
            <p className="text-base leading-relaxed">
              Start with a free 5-day trial that unlocks unlimited downloads across the entire library, then continue with a
              monthly plan or stay on the free tier with 10 downloads per month. Independent authors and research groups can
              submit their own work through the <a href="/upload" className="text-orange-600 hover:underline">Submit Document</a> portal
              and earn from downloads via Stripe Connect. See <a href="/subscriptions" className="text-orange-600 hover:underline">Pricing</a> for full details.
            </p>
          </div>
        </section>

        {/* ---- FAQ section (SEO long-tail) ---- */}
        <section
          className="border-t bg-white/40"
          style={{ borderColor: "rgba(15, 23, 42, 0.10)" }}
          data-testid="home-faq"
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 text-slate-700">
            <h2
              className="text-2xl md:text-3xl font-semibold mb-8"
              style={SERIF}
            >
              Frequently asked questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  How do I search for extractive metallurgy or mining engineering papers?
                </h3>
                <p className="text-sm leading-relaxed">
                  Use the main <a href="/search" className="text-orange-600 hover:underline">Search</a> page to type any keyword — a
                  chemical (cobalt, copper, gold, lithium), a process (leaching, flotation, solvent extraction, cyanidation),
                  an author name, or a title fragment. The engine searches inside the full text of every PDF, not just titles
                  and abstracts, so obscure references get surfaced. For faceted filtering by year, language, category, or
                  organization, use <a href="/smart-search" className="text-orange-600 hover:underline">Smart Search</a>.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  Can I read documents in French, Spanish, or other languages?
                </h3>
                <p className="text-sm leading-relaxed">
                  Documents are stored in their original language, but every item has an AI-generated abstract that you can
                  translate on-demand into English, French, Spanish, German, Portuguese, Italian, Russian, Chinese, Japanese,
                  or Arabic. Translations are cached, so re-reading is instant. The UI itself is available in English and French.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  How does the free trial work?
                </h3>
                <p className="text-sm leading-relaxed">
                  New accounts get a 5-day free trial with unlimited downloads and full library access — no credit card
                  required to start. After the trial, you can continue on the free tier with 10 downloads per month, or
                  subscribe to a paid plan (1, 3, 6, or 12 months) to keep unlimited access. Cancel anytime; access continues
                  until the end of the paid period.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  Can I upload my own thesis, article, or research report?
                </h3>
                <p className="text-sm leading-relaxed">
                  Yes. Any account holder can submit their own documents through the{" "}
                  <a href="/upload" className="text-orange-600 hover:underline">Submit Document</a> portal. Once approved by an
                  editor, your submission becomes part of the library. If your document is set as a paid download, you earn a
                  share of every purchase, paid via Stripe Connect. Universities and research groups can request a dedicated
                  organization workspace so multiple team members can submit under the same publisher.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  What file formats do you accept?
                </h3>
                <p className="text-sm leading-relaxed">
                  PDF is the primary format. Office documents (DOC, DOCX, PPT, PPTX, XLS, XLSX, ODT, RTF), plain text,
                  HTML, EPUB, and images (JPG, PNG, TIFF, WebP) are all accepted and automatically converted to PDF at upload
                  time — so your reading experience is consistent regardless of the source. Scanned or image-only PDFs are
                  OCR'd in the background so the text becomes searchable within minutes.
                </p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  How do I build a reading list or bibliography?
                </h3>
                <p className="text-sm leading-relaxed">
                  Use <a href="/collections" className="text-orange-600 hover:underline">Reading Collections</a> to group papers
                  by topic, project, or course. Collections can be private (visible only to you) or public (shared with the
                  community). Public collections are a great way to publish a bibliography for a course syllabus, a project
                  reference list, or a curated survey of an emerging research area — for example, "Battery-grade cobalt from
                  DRC ores" or "Recent advances in cyanide-free gold leaching".
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ---- Footer ---- */}
      <footer
        className="border-t mt-12"
        style={{ borderColor: "rgba(15, 23, 42, 0.10)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <span
              className="text-lg font-medium tracking-tight"
              style={SERIF}
            >
              {t.welcome}
            </span>
          </div>
          <nav className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
            <button
              onClick={() => navigate("/about")}
              data-testid="home-footer-about"
              className="hover:text-slate-900 transition-colors"
            >
              {t.footer_about}
            </button>
            <button
              onClick={() => navigate("/terms")}
              data-testid="home-footer-terms"
              className="hover:text-slate-900 transition-colors"
            >
              {t.footer_terms}
            </button>
            <a
              href="mailto:info@2ra-edition.com"
              data-testid="home-footer-contact"
              className="hover:text-slate-900 transition-colors"
            >
              {t.footer_contact}
            </a>
          </nav>
          <p className="text-xs text-slate-500">{t.footer_rights}</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
